import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { accessService } from '../../../services/central-api/access';
import { NavigationProp, RouteProp, useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import moment from 'moment';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import Header from '../../../components/Header';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import CustomButton from '../../../components/CustomButton';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';

export default function SupportTickets() {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();
    const route = useRoute<RouteProp<AccessParamList, 'SupportTickets'>>();
    const { user: authenticatedUser } = useAuth();

    const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState({
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['supportTickets', filters],
        queryFn: () => accessService.getSupportTickets(filters),
        enabled: !!authenticatedUser
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        setSupportTickets(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();
        }
    }, [isFocused, navigation, filters]));

    async function downloadReport() {
        try {
            const csv = await accessService.generateSupportTicketsReport();
            if (!(csv instanceof Blob)) {
                throw new Error();
            }
            const url = window.URL.createObjectURL(csv);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tickets-${moment(new Date()).format('YYYY-MM-DD')}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('supportTickets')}
                actions={<CustomButton
                    text={translate('downloadReport')}
                    onPress={downloadReport}
                />} />
            <Card>
                <DataTableHeader
                    title={translate('registeredSupportTickets')}
                    zIndex={2}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('subject'),
                                selector: row => row.subject,
                                grow: 2,
                                wrap: true
                            },
                            {
                                name: translate('status'),
                                selector: row => translate(row.status),
                            },
                            {
                                name: translate('priority'),
                                selector: row => translate(`${row.priority}Priority`),
                            },
                            {
                                name: translate('agentName'),
                                selector: row => row.user ? `${row.user.warName !== '' ? row.user.warName : row.user.name} - ${row.user.registry}` : '',
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                name: translate('updatedAt'),
                                selector: row => moment(row.updatedAt).format('DD/MM/YYYY HH:mm'),
                            },
                            {
                                button: true,
                                cell: row => {
                                    return <DropdownButton
                                        actions={[{
                                            content: translate('viewInformation'),
                                            onPress: () => {
                                                navigation.navigate('SupportTicketDetail', { id: row.id });
                                            }
                                        }]}
                                    />;
                                }

                            }
                        ]}
                        data={supportTickets}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}
